<script setup>
import { ref } from "vue"
import * as XLSX from "xlsx"
import FullCalendar from "@fullcalendar/vue3"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import esLocale from "@fullcalendar/core/locales/es"
import Swal from "sweetalert2"
import api from "@/api/axios.js"

const COLORES_TIPO = {
  vacaciones: "#2dce89",
  permiso: "#11cdef",
  incapacidad: "#fb6340",
  cita_medica: "#8965e0",
  otro: "#8898aa"
}

const LABELS_TIPO = {
  vacaciones: "Vacaciones",
  permiso: "Permiso",
  incapacidad: "Incapacidad",
  cita_medica: "Cita médica",
  otro: "Otro"
}

const LABELS_ESTADO = {
  pendiente: "Pendiente",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
  cancelado: "Cancelado"
}

const calendarRef = ref(null)
const exportando = ref(false)

function sumarDia(fecha) {
  const d = new Date(`${fecha.slice(0, 10)}T00:00:00`)
  d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

async function cargarEventos(info, successCallback, failureCallback) {
  try {
    const desde = info.startStr.slice(0, 10)
    const hasta = info.endStr.slice(0, 10)

    const { data } = await api.get("/rrhh/calendario", { params: { desde, hasta } })

    const eventos = data.eventos.map((ev) => ({
      id: String(ev.id),
      title: `${LABELS_TIPO[ev.tipo] || ev.tipo} - ${ev.nombres} ${ev.apellidos}`,
      start: ev.fechaInicio,
      end: sumarDia(ev.fechaFin),
      allDay: true,
      color: COLORES_TIPO[ev.tipo] || "#8898aa",
      classNames: ev.estado === "pendiente" ? ["evento-pendiente"] : []
    }))

    successCallback(eventos)
  } catch (err) {
    failureCallback(err)
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el calendario",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

function fechaLocal(d) {
  const mes = String(d.getMonth() + 1).padStart(2, "0")
  const dia = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${mes}-${dia}`
}

async function descargarExcel() {
  // Por defecto el rango del mes/semana visible en el calendario.
  const view = calendarRef.value.getApi().view
  const fin = new Date(view.currentEnd)
  fin.setDate(fin.getDate() - 1)

  const { value: rango } = await Swal.fire({
    title: "Descargar novedades",
    html: `
      <div class="text-start">
        <label class="form-label">Desde</label>
        <input id="exp-desde" type="date" class="form-control mb-3" value="${fechaLocal(view.currentStart)}">
        <label class="form-label">Hasta</label>
        <input id="exp-hasta" type="date" class="form-control" value="${fechaLocal(fin)}">
      </div>`,
    showCancelButton: true,
    confirmButtonText: "Descargar",
    cancelButtonText: "Cancelar",
    preConfirm: () => {
      const desde = document.getElementById("exp-desde").value
      const hasta = document.getElementById("exp-hasta").value
      if (!desde || !hasta) return Swal.showValidationMessage("Selecciona ambas fechas")
      if (desde > hasta) return Swal.showValidationMessage("La fecha inicial no puede ser mayor a la final")
      return { desde, hasta }
    }
  })
  if (!rango) return

  exportando.value = true
  try {
    const { data } = await api.get("/rrhh/calendario/exportar", { params: rango })

    if (!data.novedades.length) {
      Swal.fire({ icon: "info", title: "Sin novedades", text: "No hay novedades registradas en ese rango." })
      return
    }

    const filas = data.novedades.map((n) => ({
      "ID": n.id,
      "Empleado": `${n.nombres} ${n.apellidos}`,
      "Cargo": n.cargo || "",
      "Área": n.area || "",
      "Jefe inmediato": n.jefeInmediato || "",
      "Tipo": LABELS_TIPO[n.tipo] || n.tipo,
      "Estado": LABELS_ESTADO[n.estado] || n.estado,
      "Fecha inicio": n.fechaInicio,
      "Fecha fin": n.fechaFin,
      "Hora inicio": n.horaInicio || "",
      "Hora fin": n.horaFin || "",
      "Días corridos": n.diasCorridos,
      "Días solicitados": n.diasSolicitados,
      "Motivo": n.motivo || "",
      "Soporte adjunto": n.soporteNombreArchivo || "",
      "Fecha de solicitud": n.fechaSolicitud || "",
      "Registrado por": n.registradoPor || "",
      "Resuelto por": n.resueltoPor || "",
      "Fecha de resolución": n.fechaResolucion || "",
      "Comentario de resolución": n.comentarioResolucion || ""
    }))

    const hoja = XLSX.utils.json_to_sheet(filas)
    hoja["!cols"] = Object.keys(filas[0]).map((col) => ({
      wch: Math.min(50, Math.max(col.length, ...filas.map((f) => String(f[col]).length)) + 2)
    }))
    hoja["!autofilter"] = { ref: hoja["!ref"] }

    const libro = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(libro, hoja, "Novedades")
    XLSX.writeFile(libro, `Novedades_RRHH_${rango.desde}_a_${rango.hasta}.xlsx`)
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo generar el Excel",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    exportando.value = false
  }
}

const calendarOptions = {
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: "dayGridMonth",
  locale: esLocale,
  height: "auto",
  headerToolbar: {
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,dayGridWeek"
  },
  events: cargarEventos
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
        <h5 class="mb-0"><i class="ni ni-calendar-grid-58 text-primary me-2"></i>Calendario de Recursos Humanos</h5>
        <div class="d-flex gap-3 flex-wrap align-items-center">
          <span v-for="(color, tipo) in COLORES_TIPO" :key="tipo" class="d-flex align-items-center gap-1 text-sm">
            <span class="legend-dot" :style="{ background: color }"></span>{{ LABELS_TIPO[tipo] }}
          </span>
          <button class="btn btn-sm btn-success mb-0" :disabled="exportando" @click="descargarExcel">
            <i class="fas fa-file-excel me-1"></i>{{ exportando ? "Generando..." : "Descargar Excel" }}
          </button>
        </div>
      </div>
      <div class="card-body">
        <FullCalendar ref="calendarRef" :options="calendarOptions" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>

<style>
.evento-pendiente {
  opacity: 0.6;
  border-style: dashed !important;
}
</style>
